/**
 * The roadmap views on real course data, for the student home.
 *
 * {@link useRoadmapMapData} reads the course roadmap and the user's
 * completion state from CIP's contexts, folds them into
 * {@link ExpeditionWeek}s, and routes presses to the same destinations as
 * the classic todo list. The views below render that data with the coloured
 * expedition and the plain atlas with flat glyphs; CIP mounts them through
 * `AppOverrides.roadmapViews`.
 */
import {computeSectionInstant} from '@cip/core/section';
import {CompletionContext} from 'contexts/CompletionContext';
import {CourseContext} from 'contexts/CourseContext';
import {ProfileContext} from 'contexts/ProfileContext';
import {
  isAdmin,
  isATypeOfStudent,
  roleToFirebaseString,
} from 'contexts/profileUtil';
import {useRoadmapStyle} from 'contexts/RoadmapStyleContext';
import {ONE_HOUR_MS, useServerTimeOnce} from 'contexts/TimeContext';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {AssnContext} from 'course/contexts/AssnContext';
import {RoadmapContext} from 'course/contexts/RoadmapContext';
import {getRoadmapItemHref} from 'course/editors/roadmapEditor/roadmapLinks';
import {
  getMathAssignmentPath,
  getOneOfChoices,
} from 'course/editors/roadmapEditor/roadmapOneOf';
import {
  getReleaseState,
  toRoadmapClockString,
} from 'course/editors/roadmapEditor/releaseStatus';
import {ROADMAP} from 'course/editors/roadmapEditor/roadmapSchema';
import type {
  RoadmapItem,
  RoadmapModule,
} from 'course/editors/roadmapEditor/roadmapSchema';
import {chooseOneOfAssignment} from 'course/splash/components/oneOfChooser';
import {roadmapInAppPath} from 'course/splash/components/RoadmapLinkCallout';
import {RoadmapSkeleton} from 'course/splash/components/RoadmapSkeleton';
import {useHistoryNavigate} from 'hooks/router/useHistoryNavigate';
import {useCourseId} from 'hooks/router/useUrlParams';
import {useCompletionMap} from 'hooks/user/useCompletion';
import {useAuthUid} from 'hooks/user/useUserId';
import {useCallback, useContext, useMemo, useRef, type ReactNode} from 'react';
import {Role} from 'types/role';
import {AtlasRoadmap} from './AtlasRoadmap';
import {ExpeditionRoadmap} from './ExpeditionRoadmap';
import {useLookAndFeel} from './lookAndFeel/useLookAndFeel';
import type {
  ExpeditionItem,
  ExpeditionItemKind,
  ExpeditionWeek,
} from './expeditionModel';
import {findNextItem} from './expeditionModel';
import {useRoadmapStartPosition} from './useRoadmapStartPosition';

/**
 * Maps a roadmap item type to the map's item kind.
 *
 * @param itemType - The roadmap item's type.
 * @return The kind that picks its icon and click behaviour.
 */
function kindOf(itemType: RoadmapItem['itemType']): ExpeditionItemKind {
  switch (itemType) {
    case ROADMAP.LESSON.key:
      return 'learn';
    case ROADMAP.ASSIGNMENT.key:
    case ROADMAP.CREATE_ASSIGNMENT.key:
      return 'code';
    case ROADMAP.MATH_ASSIGNMENT.key:
    case ROADMAP.ONE_OF.key:
      return 'math';
    case ROADMAP.SECTION.key:
      return 'section';
    case ROADMAP.UNTIED_LINK.key:
      return 'link';
    default:
      return 'check';
  }
}

/**
 * Formats a module's release date as a short label, e.g. "Oct 9".
 *
 * @param startDate - The module's ISO-like start date.
 * @return The label, or an empty string when the date does not parse.
 */
function dateLabel(startDate: string): string {
  const date = new Date(startDate);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
}

/**
 * Whether the student can still finish a roadmap item. Plain links record
 * no completion when opened and create-assignment items have no completion
 * rule, so neither ever can. A section can until the student's own meeting
 * for its week is over; it is treated as over an hour after it starts, the
 * grace `computeNextSectionSlot` defaults to.
 *
 * @param args.item - The roadmap item.
 * @param args.sectionTimeIndex - The student's section slot. When omitted
 *   (no section, or one without a time), no section item is finishable.
 * @param args.firstSectionTimestamp - The course's section anchor.
 * @param args.nowMs - The current time.
 * @return Whether the item can still be finished.
 * @throws Never.
 */
function isFinishable({
  item,
  sectionTimeIndex,
  firstSectionTimestamp,
  nowMs,
}: {
  item: RoadmapItem;
  sectionTimeIndex?: number;
  firstSectionTimestamp: string;
  nowMs: number;
}): boolean {
  switch (item.itemType) {
    case ROADMAP.UNTIED_LINK.key:
    case ROADMAP.CREATE_ASSIGNMENT.key:
      return false;
    case ROADMAP.SECTION.key: {
      // The section item's completion id is its 0-based section week.
      const weekIndex = Number(item.completionId);
      if (
        sectionTimeIndex === undefined ||
        !item.completionId ||
        !Number.isInteger(weekIndex) ||
        Number.isNaN(Date.parse(firstSectionTimestamp))
      ) {
        return false;
      }
      const startMs = computeSectionInstant({
        firstSectionTimestamp,
        timeIndex: sectionTimeIndex,
        weekIndex,
      }).getTime();
      return nowMs < startMs + ONE_HOUR_MS;
    }
    default:
      return true;
  }
}

/**
 * The item the home opens on: the student's next item or, when nothing
 * finishable is left, the last main-line item released.
 *
 * @param args.weeks - Weeks in course order.
 * @param args.completed - Ids of completed items.
 * @return The item's id, or undefined when no week has items.
 * @throws Never.
 */
function startItemIdOf({
  weeks,
  completed,
}: {
  weeks: readonly ExpeditionWeek[];
  completed: ReadonlySet<string>;
}): string | undefined {
  const released = weeks
    .filter(week => !week.isLocked)
    .flatMap(week => week.main);
  return (findNextItem({weeks, completed}) ?? released[released.length - 1])
    ?.id;
}

/**
 * The student's modules in course order, with their lock state.
 *
 * Mirrors the classic list's filtering: only modules for the viewer's
 * roadmap type, sorted by release date. Unlike the classic list, unreleased
 * modules are kept and shown locked: every one for staff, and the next week
 * for a student, which the roadmap context loads apart from the released
 * ones.
 *
 * @param args.roadmapItemMap - All loaded modules, by id.
 * @param args.role - The viewer's course role.
 * @param args.nowMs - The server time, or undefined while it loads (every
 *     module locked).
 * @return Modules with ids, oldest first.
 */
function studentModules({
  roadmapItemMap,
  role,
  nowMs,
}: {
  roadmapItemMap: Record<string, RoadmapModule>;
  role: Role;
  nowMs: number | undefined;
}): Array<{id: string; module: RoadmapModule; isLocked: boolean}> {
  const roadmapType = roleToFirebaseString(
    isATypeOfStudent(role) ? role : Role.STUDENT,
  );
  const nowClock =
    nowMs === undefined ? undefined : toRoadmapClockString({nowMs});
  return Object.entries(roadmapItemMap)
    .filter(([, module]) => module.roadmapType === roadmapType)
    .sort(([, a], [, b]) => (a.startDate > b.startDate ? 1 : -1))
    .map(([id, module]) => ({
      id,
      module,
      isLocked:
        nowClock === undefined ||
        getReleaseState({startDate: module.startDate, nowClock}) !== 'released',
    }));
}

/** What a map view needs to render the course roadmap. */
export interface RoadmapMapData {
  weeks: ExpeditionWeek[];
  completed: ReadonlySet<string>;
  onSelect: (item: ExpeditionItem) => void;
  isLoading: boolean;
  /**
   * Whether the viewer may open items in locked weeks: true for a course
   * admin, so they can try an item before it releases. A staff view-as
   * profile carries a student role, so it sees the student's locks.
   */
  canOpenLocked: boolean;
}

/**
 * Folds the roadmap and completion contexts into map data.
 *
 * @return The weeks, completed ids, a select handler, and a loading flag.
 */
export function useRoadmapMapData(): RoadmapMapData {
  const {roadmapItemMap, nextRoadmapItemMap, roadmapLoading} =
    useContext(RoadmapContext);
  const {
    userData,
    sectionData,
    loading: profileLoading,
  } = useContext(ProfileContext);
  const {firstSectionTimestamp} = useContext(CourseContext);
  const {setSelfCheckCompleted, isAssnCompleted, isCompletionLoading} =
    useContext(CompletionContext);
  const {assnMetaData} = useContext(AssnContext);
  const t = useTranslateFunction('course');
  const nowMs = useServerTimeOnce();
  const courseId = useCourseId();
  const navigate = useHistoryNavigate();

  const modules = useMemo(
    () =>
      studentModules({
        roadmapItemMap: {...nextRoadmapItemMap, ...roadmapItemMap},
        role: userData?.courseRole ?? Role.STUDENT,
        nowMs,
      }),
    [roadmapItemMap, nextRoadmapItemMap, userData?.courseRole, nowMs],
  );

  const itemsById = useMemo(() => {
    const map = new Map<string, RoadmapItem>();
    for (const {module} of modules) {
      for (const item of module.items) {
        if (item.published) {
          map.set(item.id, item);
        }
      }
    }
    return map;
  }, [modules]);

  const {completionMap} = useCompletionMap([...itemsById.values()]);
  const completed = useMemo(
    () =>
      new Set(
        Object.keys(completionMap).filter(id => Boolean(completionMap[id])),
      ),
    [completionMap],
  );

  const sectionId = userData?.courseSections?.[0]?.id;
  const timeIndex = sectionId ? sectionData?.[sectionId]?.timeIndex : undefined;
  const sectionTimeIndex =
    typeof timeIndex === 'number' ? timeIndex : undefined;

  const weeks = useMemo<ExpeditionWeek[]>(
    () =>
      modules.map(({id, module, isLocked}) => {
        const toItem = (item: RoadmapItem): ExpeditionItem => ({
          id: item.id,
          kind: kindOf(item.itemType),
          title: item.title,
          blurb: item.description,
          href:
            kindOf(item.itemType) === 'link'
              ? getRoadmapItemHref({item, courseId})
              : undefined,
          isFinishable: isFinishable({
            item,
            sectionTimeIndex,
            firstSectionTimestamp,
            nowMs: nowMs ?? Number.NaN,
          }),
        });
        const published = module.items.filter(item => item.published);
        return {
          id,
          label: dateLabel(module.startDate),
          title: module.title,
          isLocked,
          main: published.filter(item => !item.isOptional).map(toItem),
          goingFurther: published.filter(item => item.isOptional).map(toItem),
        };
      }),
    [modules, sectionTimeIndex, firstSectionTimestamp, nowMs, courseId],
  );

  const handleSelect = useCallback(
    async (selected: ExpeditionItem) => {
      const item = itemsById.get(selected.id);
      if (!item) {
        return;
      }
      if (item.itemType === ROADMAP.ONE_OF.key) {
        const picked = await chooseOneOfAssignment({
          title: item.title,
          description: item.description,
          choices: getOneOfChoices(item).map(id => ({
            id,
            title: assnMetaData?.[id]?.title ?? id,
            tagline: assnMetaData?.[id]?.tagline,
            icon: assnMetaData?.[id]?.icon,
            picture: assnMetaData?.[id]?.splashImage,
            note: assnMetaData?.[id]?.chooserNote,
            isComplete: isAssnCompleted(id) === true,
          })),
          t,
        });
        if (picked !== null) {
          navigate(getMathAssignmentPath({courseId, assignmentId: picked}));
        }
        return;
      }
      if (item.itemType === ROADMAP.COMPLETION_LINK.key) {
        await setSelfCheckCompleted(item.completionId ?? item.urlId ?? '');
      }
      const destination = getRoadmapItemHref({item, courseId});
      const path = roadmapInAppPath(destination);
      if (path === null) {
        window.open(destination, '_blank', 'noopener');
        return;
      }
      navigate(path);
    },
    [
      itemsById,
      setSelfCheckCompleted,
      isAssnCompleted,
      assnMetaData,
      t,
      courseId,
      navigate,
    ],
  );

  return {
    weeks,
    completed,
    onSelect: handleSelect,
    canOpenLocked: isAdmin(userData?.courseRole ?? Role.STUDENT),
    // Completion decides where the START marker and the opening position
    // land, so the map waits for it rather than moving them once it arrives.
    isLoading:
      roadmapLoading ||
      profileLoading ||
      isCompletionLoading ||
      nowMs === undefined,
  };
}

/**
 * Wraps a map so the page opens already scrolled to the student's start
 * item, or to where they left this view earlier in the tab.
 *
 * @param props.weeks - The weeks the map renders.
 * @param props.completed - Ids of completed items.
 * @param props.children - The map.
 * @return The wrapped map.
 * @throws Never.
 */
function OpensAtStartItem({
  weeks,
  completed,
  children,
}: {
  weeks: readonly ExpeditionWeek[];
  completed: ReadonlySet<string>;
  children: ReactNode;
}) {
  const viewRef = useRef<HTMLDivElement>(null);
  const {roadmapStyle} = useRoadmapStyle();
  const uid = useAuthUid();
  const courseId = useCourseId();
  useRoadmapStartPosition({
    viewRef,
    memoryKey: `${uid}/${courseId}/${roadmapStyle}`,
    startItemId: startItemIdOf({weeks, completed}),
  });
  return <div ref={viewRef}>{children}</div>;
}

/** The plain atlas with the classic list's flat icons, on real course data. */
export function PlainFlatRoadmapView() {
  const {weeks, completed, onSelect, isLoading, canOpenLocked} =
    useRoadmapMapData();
  const {lookAndFeel, isLoading: isLookLoading} = useLookAndFeel(useCourseId());
  if (isLoading || isLookLoading) {
    return <RoadmapSkeleton />;
  }
  return (
    <OpensAtStartItem weeks={weeks} completed={completed}>
      <AtlasRoadmap
        weeks={weeks}
        completed={completed}
        onSelect={onSelect}
        variant="plain"
        icons="flat"
        lookAndFeel={lookAndFeel}
        canOpenLocked={canOpenLocked}
      />
    </OpensAtStartItem>
  );
}

/** The coloured expedition on real course data. */
export function ExpeditionRoadmapView() {
  const {weeks, completed, onSelect, isLoading, canOpenLocked} =
    useRoadmapMapData();
  const {lookAndFeel, isLoading: isLookLoading} = useLookAndFeel(useCourseId());
  if (isLoading || isLookLoading) {
    return <RoadmapSkeleton />;
  }
  return (
    <OpensAtStartItem weeks={weeks} completed={completed}>
      <ExpeditionRoadmap
        weeks={weeks}
        completed={completed}
        onSelect={onSelect}
        lookAndFeel={lookAndFeel}
        canOpenLocked={canOpenLocked}
      />
    </OpensAtStartItem>
  );
}
